import React, { useState } from 'react';
import { useAppSelector } from '../../../../../redux/hooks';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, TextField,
  Button, CircularProgress, Select, MenuItem, FormControl, InputLabel, useMediaQuery
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';

interface CreateTemplateModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: { title: string; language: string; pastedText?: string }, isFromText: boolean) => void;
  isSubmitting: boolean;
}

const CreateTemplateModal: React.FC<CreateTemplateModalProps> = ({ open, onClose, onSubmit, isSubmitting }) => {
  const { t } = useTranslation();
  const colors = useAppSelector((state) => state.theme.colors);
  const isMobile = useMediaQuery('(max-width: 600px)');

  const [newTitle, setNewTitle] = useState('');
  const [pastedText, setPastedText] = useState('');
  const [language, setLanguage] = useState('English');

  const handleSubmit = (isFromText: boolean) => {
    onSubmit({ title: newTitle, language, pastedText }, isFromText);
  };

  const handleClose = () => {
    setNewTitle('');
    setPastedText('');
    setLanguage('English');
    onClose();
  };

  const inputStyles = {
    '& .MuiInputBase-input': { color: colors?.text },
    '& .MuiInputLabel-root': { color: colors?.listInfo },
    '& .MuiOutlinedInput-root': {
      backgroundColor: colors?.interface,
      '& fieldset': { borderColor: colors?.folderDataRowBorderColor || '#DDE1E6' },
      '&:hover fieldset': { borderColor: colors?.lightBlue },
      '&.Mui-focused fieldset': { borderColor: colors?.lightBlue, borderWidth: '2px' },
    },
    '& .MuiSelect-icon': { color: colors?.listInfo },
  };

  const primaryButtonStyle = {
    backgroundColor: colors?.lightBlue, 
    '&:hover': { backgroundColor: colors?.darkBlue }, 
    fontWeight: 'bold', 
    borderRadius: 2,
    '&.Mui-disabled': {
      backgroundColor: colors?.legend,
      color: colors?.disabledIcon
    },
    width: isMobile ? '100%' : 'auto',
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      // FIX: This positions the dialog container at the top of the viewport
      sx={{
        '& .MuiDialog-container': {
          alignItems: 'flex-start',
        },
      }}
      PaperProps={{
        sx: {
          backgroundColor: colors?.container,
          borderRadius: 4,
          border: `1px solid ${colors?.folderDataRowBorderColor}`,
          // FIX: This adds margin from the top, pushing the dialog down
          mt: '10vh',
        }
      }}
    >
      <DialogTitle sx={{ color: colors?.text, fontWeight: 'bold', borderBottom: `1px solid ${colors?.folderDataRowBorderColor}` }}>
        {t('createNewTemplate')}
      </DialogTitle>
      <DialogContent sx={{ pt: '20px !important', backgroundColor: colors?.container }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: isMobile ? 'column' : 'row' }}>
            <TextField
              label={t('templateTitle')}
              variant="outlined"
              fullWidth
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              disabled={isSubmitting}
              sx={inputStyles}
            />
            <FormControl sx={{ minWidth: 150, ...inputStyles }}>
              <InputLabel>{t('language')}</InputLabel>
              <Select
                value={language}
                label={t('language')}
                onChange={(e) => setLanguage(e.target.value)}
                disabled={isSubmitting}
                MenuProps={{
                  PaperProps: {
                    sx: { 
                      backgroundColor: colors?.container,
                      color: colors?.text,
                      border: `1px solid ${colors?.folderDataRowBorderColor}`,
                      borderRadius: 2,
                    }
                  }
                }}
              >
                <MenuItem value="English" sx={{ '&:hover': { backgroundColor: colors?.sectionHover }}}>English</MenuItem>
                <MenuItem value="French" sx={{ '&:hover': { backgroundColor: colors?.sectionHover }}}>Français</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <TextField
            label={t('pasteExampleNote')}
            variant="outlined"
            fullWidth
            multiline
            rows={10}
            value={pastedText}
            onChange={(e) => setPastedText(e.target.value)}
            placeholder={String(t('exampleNotePlaceholder'))}
            disabled={isSubmitting}
            sx={inputStyles}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: '16px 24px', borderTop: `1px solid ${colors?.folderDataRowBorderColor}`, flexDirection: isMobile ? 'column-reverse' : 'row', gap: isMobile ? 1.5 : 1 }}>
        <Button 
          variant="contained"
          onClick={handleClose} 
          sx={{...primaryButtonStyle, width: isMobile ? '100%' : 'auto' }}
        >
          {t('cancel')}
        </Button>
        <Box sx={{ flexGrow: 1, display: isMobile ? 'none' : 'block' }} />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleSubmit(false)}
          disabled={!newTitle.trim() || isSubmitting}
          sx={primaryButtonStyle}
        >
          {t('createEmpty')}
        </Button>
        <Button
          variant="contained"
          startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <AutoFixHighIcon />}
          onClick={() => handleSubmit(true)}
          disabled={!newTitle.trim() || !pastedText.trim() || isSubmitting}
          sx={primaryButtonStyle}
        >
          {isSubmitting ? t('analyzing') : t('createFromText')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateTemplateModal;